import React, {
  useCallback, useEffect, useState,
} from 'react';
import {
  Button,
  Col,
  Offcanvas,
  Row,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { DocRequestEntity, DocRequestEntityLog, DocRequestStatus } from 'types/DocRequest';
import handlePromise from 'utils/Promise';
import DocsRequestService from 'services/api/DocsRequest';
import { isValidDate } from 'utils/date';
import moment from 'moment';
import { BsList } from 'react-icons/bs';
import { FiUser } from 'react-icons/fi';
import { IoEyeOutline } from 'react-icons/io5';
import classNames from 'classnames';
import { processDocRequestLogs } from 'utils/docLogs';
import DocStatusBadge from '../StatusBadge';

type DownloadFileProps = {
  handleViewDocument: (
    documentoId: number,
    cuentaDeInversionID: number,
    url: string,
    nombreArchivo: string,
    tipoArchivo: string,
    descargar: boolean,
  ) => void;
};
type DocDetailOffcanvasProps = {
  data?: DocRequestEntity,
  onClose: () => void,
};

const WRAP_LONG_TEXT_STYLE: React.CSSProperties = {
  whiteSpace: 'pre-wrap',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

function DetailItem( { title, desc, isDate }: { title: string, desc?: string, isDate?: boolean } ) {
  const dateLabel = isValidDate( desc ) ? moment( new Date( desc ) ).format( 'DD/MM/YYYY' ) : '-';
  return (
    <>
      <p className="text-primary text--sm m-0 d-flex align-items-center gap-2 mb-2">
        <BsList size={18} />
        {title}
      </p>
      <p className="text-gray30 text--sm fw-medium">
        {!isDate ? desc || '-' : dateLabel }
      </p>
    </>
  );
}

function DetailItemStatus( {
  id,
  fechaAccion, nota, estado, autorAccionName, descripcion, accion, nombreArchivo, fechaLimite,
  urlArchivo,
  tipoArchivo,
  cuentaDeInversionID,
  fechaExpiracion, handleViewDocument,
}: DocRequestEntityLog & DownloadFileProps ) {
  const { t } = useTranslation();
  return (
    <Row className="gx-3 align-items-center mb-4 log">
      <Col xs="auto">
        <div className="user-avatar">
          <FiUser size={16} />
        </div>
      </Col>
      <Col xs>
        <Row className="mb-1">
          <Col xs>
            <p className="text--black10 text--xs m-0">
              {moment( new Date( fechaAccion ) ).format( 'DD/MM/YYYY' )}
              {' '}
              {moment( new Date( fechaAccion ) ).format( 'HH:mm' )}
            </p>
          </Col>
          <Col xs="auto">
            <p className="text--black10 text--xs m-0">
              {autorAccionName}
            </p>
          </Col>
        </Row>

        {accion === 'ARCHIVO_EDITADO' && (
        <Row>
          <Col xs className="d-flex align-items-center gap-1">
            <p className="text--sm m-0" style={WRAP_LONG_TEXT_STYLE}>
              <span className="text--medium">{t( 'admin.docs.field.file' )}</span>
              {': '}
              {nombreArchivo}
            </p>
            <Button
              className="px-2 py-0"
              variant="link"
              disabled={!( !!nombreArchivo && !!urlArchivo )}
              onClick={() => {
                if ( !urlArchivo || !nombreArchivo
                      || !tipoArchivo
                ) return;
                handleViewDocument(
                  id * 1000,
                  cuentaDeInversionID,
                  urlArchivo,
                  nombreArchivo,
                  tipoArchivo,
                  false,
                );
              }}
            >
              <IoEyeOutline />
            </Button>
          </Col>
        </Row>
        )}

        {accion === 'EDITADA' && descripcion && (
          <Row>
            <Col xs>
              <p className="text--sm m-0" style={WRAP_LONG_TEXT_STYLE}>
                <span className="text--medium">{t( 'admin.docs.field.description' )}</span>
                {': '}
                {descripcion}
              </p>
            </Col>
          </Row>
        )}

        {accion === 'EDITADA' && fechaLimite && (
          <Row>
            <Col xs="auto">
              <p className="text--sm m-0">
                <span className="text--medium">{t( 'admin.docs.field.limitDate' )}</span>
                {': '}
                {fechaLimite ? moment( new Date( fechaLimite ) ).format( 'DD/MM/YYYY' ) : '-'}
              </p>
            </Col>
          </Row>
        )}

        {accion === 'EDITADA' && fechaExpiracion && (
          <Row>
            <Col xs="auto">
              <p className="text--sm m-0">
                <span className="text--medium">{t( 'admin.docs.field.expirationDate' )}</span>
                {': '}
                {fechaExpiracion ? moment( new Date( fechaExpiracion ) ).format( 'DD/MM/YYYY' ) : '-'}
              </p>
            </Col>
          </Row>
        )}

        {accion === 'EDITADA' && estado && (
        <Row>
          <Col xs="auto">
            <DocStatusBadge
              status={estado as DocRequestStatus}
            />
          </Col>
        </Row>
        )}
        {!!nota
        && (
        <p className="text--medium text--xs mb-0 mt-1 text--gray26">
          {nota}
        </p>
        )}

      </Col>
    </Row>
  );
}

function DocDetailOffcanvas( {
  data: item,
  onClose,
  handleViewDocument,
}: DocDetailOffcanvasProps & DownloadFileProps ) {
  const { t } = useTranslation();
  const [data, setData] = useState<DocRequestEntity>();
  const [loading, setLoading] = useState( false );

  const fetchData = useCallback( async () => {
    if ( item?.cuentaDeInversionID && item?.id ) {
      setLoading( true );
      const [, response, responseData] = await handlePromise(
        DocsRequestService.getDocRequestsById( item.cuentaDeInversionID, item.id ),
      );

      if ( response.ok ) {
        const originalLogs: DocRequestEntityLog[] = responseData.logs || [];

        const allProcessedLogs = processDocRequestLogs( originalLogs );

        const logsForUI = ( allProcessedLogs as DocRequestEntityLog[] )
          .filter( ( log: DocRequestEntityLog ) => log.accion !== 'CREADA' );

        setData( {
          ...responseData,
          logs: logsForUI,
        } );
      }
      setLoading( false );
    }
  }, [item?.cuentaDeInversionID, item?.id] );

  useEffect( () => {
    fetchData();
  }, [] );

  return (
    <Offcanvas
      show
      placement="end"
      onHide={onClose}
      className="offcanvas-docs"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Documentos requeridos</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className={classNames( 'pt-0', {
        'app-component-loading': loading,
      } )}
      >
        <DetailItem
          title={t( 'admin.docs.field.id' )}
          desc={data?.id?.toString()}
        />
        <DetailItem
          title={t( 'admin.docs.field.description' )}
          desc={data?.descripcion}
        />
        <DetailItem
          title={t( 'admin.docs.field.lastRevision' )}
          desc={data?.fechaUltimaRevision}
          isDate
        />
        <DetailItem
          title={t( 'admin.docs.field.limitDate' )}
          desc={data?.fechaLimite}
          isDate
        />
        <DetailItem
          title={t( 'admin.docs.field.expirationDate' )}
          desc={data?.fechaExpiracion}
          isDate
        />

        {!!data?.logs?.length && <hr />}
        {data?.logs?.map( ( log ) => {
          const logConCuenta = {
            ...log,
            cuentaDeInversionID: data!.cuentaDeInversionID,
          };

          return (
            <DetailItemStatus
              key={log.id as number}
              handleViewDocument={handleViewDocument}
              {...logConCuenta}
            />
          );
        } )}
      </Offcanvas.Body>
    </Offcanvas>
  );
}

export default DocDetailOffcanvas;
